import Tracer from './utils/tracer';
import type { IEventBus } from '@shore/types';

interface EventBusRequestEvent {
  payload: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

// Payload/response stay Record<string, unknown>/unknown rather than generic
// type params — nothing downstream needs the specific shape back (Tracer's
// payload param and IEventBus.emit's payload param are already
// Record<string, unknown>/any), so a generic here would buy no real safety.
function withCallbacks(eventBus: IEventBus) {
  return (successEvent: string, errorEvent: string) =>
    (callbackFn: (payload: Record<string, unknown>) => Promise<unknown>) =>
    ({ payload, metadata = {} }: EventBusRequestEvent): Promise<void> => {
      Tracer.addAction('api.event_bus.request', {
        eventBus: {
          payload,
          metadata,
        },
      });

      return callbackFn(payload)
        .then((response) => {
          Tracer.addAction('api.event_bus.success_response', {
            eventBus: {
              payload,
              metadata: {
                ...metadata,
                event: successEvent,
              },
            },
          });

          eventBus.emit(successEvent, response);
        })
        .catch((error: unknown) => {
          console.error(error);

          Tracer.addAction('api.event_bus.error_response', {
            eventBus: {
              payload,
              metadata: {
                ...metadata,
                event: errorEvent,
              },
            },
          });

          eventBus.emit(errorEvent, error);
        });
    };
}

type EventCallbackMap = Record<
  string,
  (event: EventBusRequestEvent) => Promise<void>
>;

export type EventCallbackMapFactory = (
  withCallbacksFn: ReturnType<typeof withCallbacks>
) => EventCallbackMap;

export default function apiEventBus(
  createEventCallbackMap: EventCallbackMapFactory
) {
  return (eventBus: IEventBus): { unsubscribe: () => void } => {
    const subscribedEventsList = Object.entries(
      createEventCallbackMap(withCallbacks(eventBus))
    );

    subscribedEventsList.forEach(([event, callback]) => {
      eventBus.on(event, callback);
    });

    return {
      unsubscribe: () => {
        subscribedEventsList.forEach(([event, callback]) => {
          eventBus.off(event, callback);
        });
      },
    };
  };
}
